import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Banner, Button, Modal, StatusBadge, Spinner, cn } from '@immo/ui-kit';
import {
  FLEET_JOB_MAX_RUNS,
  type FleetBoardBlocker,
  type FleetBoardRun,
  type FleetBoardRunQuestion,
  type GetFleetBoardTicketResponse,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { useLocaleFormat } from '@/i18n/use-locale-format';
import { AlertCircleIcon, ExternalLinkIcon, SparkleIcon, XIcon } from '@/components/layout/icons';
import { formatDuration } from '@/lib/landiq/format-duration';
import { firstLine, runDurationMs, runUsage } from '@/lib/fleet/ticket-runs';
import type { FoundCard } from '@/lib/fleet/board-queue';
import { CardTag } from '@/components/fleet/board/card-tag';
import { RunDetail } from '@/components/fleet/board/run-detail';
import { QuestionAnswer } from '@/components/fleet/board/question-answer';
import { AssistantComposer } from '@/components/fleet/board/assistant-composer';
import { AssistantMessageList } from '@/components/fleet/board/assistant-message-list';
import { useAssistantConversation } from '@/components/fleet/board/use-assistant-conversation';

type Props = {
  /** The Linear identifier, from `?job=`. */
  readonly ticket: string;
  /** The card the board shows for it now; `null` when it is on no lane. */
  readonly found: FoundCard | null;
  /** Changes with each board poll: the ticket's runs are re-read with it. */
  readonly refreshKey: unknown;
  readonly canCommand: boolean;
  readonly onClose: () => void;
  /** Opens another ticket in this panel: a blocker's, or a slice ref's parent. */
  readonly onOpenTicket: (ticket: string) => void;
};

type TicketRead = {
  readonly ticket: string;
  readonly data: GetFleetBoardTicketResponse | null;
  readonly failed: boolean;
};

function Field({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-xs text-text-tertiary">{label}</dt>
      <dd className="min-w-0 break-words text-sm text-text-primary">{children}</dd>
    </div>
  );
}

function Blockers({
  blockers,
  onOpen,
}: {
  readonly blockers: ReadonlyArray<FleetBoardBlocker>;
  readonly onOpen: (ticket: string) => void;
}) {
  const t = useTranslations('pages.admin.fleet-board');
  const tp = useTranslations('pages.admin.fleet-board.panel');

  if (blockers.length === 0) return null;

  return (
    <section className="flex flex-col gap-2" aria-label={tp('blockers-heading')}>
      <h3 className="text-sm font-semibold text-text-primary">{tp('blockers-heading')}</h3>
      <ul className="flex flex-col gap-2">
        {blockers.map((blocker) => {
          const target = blocker.parent ?? blocker.identifier;

          return (
            <li
              key={blocker.identifier}
              className="flex min-w-0 flex-col gap-1 rounded-md border border-border bg-canvas px-3 py-2"
            >
              <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                <button
                  type="button"
                  onClick={() => onOpen(target)}
                  title={tp('blocker-open', { ticket: target })}
                  className="font-mono text-sm text-text-primary hover:underline"
                >
                  {blocker.identifier}
                </button>
                {blocker.parent !== null && <StatusBadge size="sm" tone="info" label={tp('blocker-slice')} />}
                <StatusBadge size="sm" tone="neutral" label={blocker.state} />
                {blocker.tag !== null && <CardTag tag={blocker.tag} />}
                <a
                  href={blocker.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('linear-label', { ticket: target })}
                  title={t('linear')}
                  className="ml-auto inline-flex shrink-0 items-center text-text-tertiary hover:text-text-primary"
                >
                  <ExternalLinkIcon aria-hidden className="h-3.5 w-3.5" />
                </a>
              </span>
              <span className="min-w-0 truncate text-xs text-text-secondary" title={blocker.title}>
                {blocker.title}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** The operator's conversation with the assistant, narrowed to one question; what they write here has it as context. */
function QuestionConversation({
  questionId,
  onAnswerRecorded,
}: {
  readonly questionId: string;
  readonly onAnswerRecorded: () => void;
}) {
  const ta = useTranslations('pages.admin.fleet-board.assistant');
  const tErrors = useTranslations('errors');
  const { messages, pending, readFailed, send } = useAssistantConversation(questionId);
  const recorded = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (messages === null) return;

    const ids = messages.filter((message) => message.body.kind === 'answer-recorded').map((message) => message.id);

    if (recorded.current === null) {
      recorded.current = new Set(ids);

      return;
    }

    const seen = recorded.current;
    const fresh = ids.filter((id) => !seen.has(id));

    for (const id of fresh) seen.add(id);

    if (fresh.length > 0) onAnswerRecorded();
  }, [messages, onAnswerRecorded]);

  return (
    <section aria-label={ta('title')} className="mt-2 flex flex-col gap-2 border-t border-border pt-2">
      <p className="flex items-center gap-1.5 text-xs text-text-tertiary">
        <SparkleIcon aria-hidden className="h-3.5 w-3.5" />
        {ta('title')}
      </p>
      {messages === null && readFailed && (
        <Banner variant="danger" icon={<AlertCircleIcon />} title={tErrors('generic.unexpected')} />
      )}
      {messages !== null && (messages.length > 0 || pending) && (
        <AssistantMessageList messages={messages} pending={pending} scrollToLatest={false} />
      )}
      <AssistantComposer onSend={send} />
    </section>
  );
}

function Questions({
  questions,
  ticket,
  open,
  canCommand,
  onResolved,
}: {
  readonly questions: ReadonlyArray<FleetBoardRunQuestion>;
  readonly ticket: string;
  /** Whether the run's unanswered questions may still be answered: no later run of its key claimed it. */
  readonly open: boolean;
  readonly canCommand: boolean;
  readonly onResolved: () => void;
}) {
  const t = useTranslations('pages.admin.fleet-board.panel');
  const tj = useTranslations('pages.admin.jobs');
  const { moment } = useLocaleFormat();

  if (questions.length === 0) return null;

  return (
    <section className="flex flex-col gap-2" aria-label={t('questions')}>
      <h3 className="text-sm font-semibold text-text-primary">{t('questions')}</h3>
      <ul className="flex flex-col gap-2">
        {questions.map((question) => (
          <li key={question.id} className="flex flex-col gap-1 rounded-md border border-border bg-canvas px-3 py-2">
            {/* The ticket's product TL;DR (OPS-87): what it does for the user, before the engineering problem. */}
            {question.brief !== null && (
              <p className="break-words text-xs text-text-secondary">
                {t('question-brief', { brief: question.brief })}
              </p>
            )}
            <p className="whitespace-pre-wrap break-words text-sm text-text-primary">{question.problem}</p>
            <p className="text-xs text-text-tertiary">{t('asked-at', { time: moment(question.askedAt) })}</p>
            {question.answer === null ? (
              <>
                <p className="text-xs text-warning-700">{t('unanswered')}</p>
                {open && canCommand && <QuestionAnswer question={question} ticket={ticket} onResolved={onResolved} />}
                {open && !canCommand && <p className="text-xs text-text-tertiary">{tj('question-read-only-note')}</p>}
              </>
            ) : (
              <>
                <p className="whitespace-pre-wrap break-words text-sm text-text-primary">{question.answer}</p>
                {question.answeredBy !== null && question.answeredAt !== null && (
                  <p className="text-xs text-text-tertiary">
                    {t('answered-by', { name: question.answeredBy, time: moment(question.answeredAt) })}
                  </p>
                )}
              </>
            )}
            {canCommand && <QuestionConversation questionId={question.id} onAnswerRecorded={onResolved} />}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Hands a held job back (IMM-938): the way out of a round that died without closing its job. */
function ReleaseJob({ jobId, onReleased }: { readonly jobId: string; readonly onReleased: () => void }) {
  const t = useTranslations('pages.admin.jobs');
  const tErrors = useTranslations('errors');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const release = async () => {
    setBusy(true);
    setError(null);

    try {
      const result = await apiClient.adminFleetJobs.release({ pathParams: { jobId }, body: {} });

      if (result.ok) {
        setConfirming(false);
        onReleased();

        return;
      }

      const code = (result.body as { error?: { code?: string } } | undefined)?.error?.code;
      setError(code !== undefined ? tErrors(code) : t('generic-error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div>
        <Button type="button" size="sm" variant="danger" onClick={() => setConfirming(true)}>
          {t('release-cta')}
        </Button>
      </div>
      {confirming && (
        <Modal
          open
          onClose={() => (busy ? undefined : setConfirming(false))}
          confirmOnClose={false}
          size="sm"
          title={t('release-title')}
          footer={
            <>
              <Button variant="ghost" type="button" onClick={() => setConfirming(false)} disabled={busy}>
                {t('cancel-cta')}
              </Button>
              <Button type="button" variant="danger" onClick={() => void release()} disabled={busy}>
                {t('release-confirm')}
              </Button>
            </>
          }
        >
          <p className="text-sm text-text-secondary">{t('release-body')}</p>
          {error !== null && (
            <p role="alert" className="mt-3 text-sm text-danger-600">
              {error}
            </p>
          )}
        </Modal>
      )}
    </>
  );
}

function RunRow({
  run,
  selected,
  now,
  onSelect,
}: {
  readonly run: FleetBoardRun;
  readonly selected: boolean;
  readonly now: number;
  readonly onSelect: () => void;
}) {
  const t = useTranslations('pages.admin.fleet-board.panel');
  const { moment, number, euro } = useLocaleFormat();
  const usage = runUsage(run);
  const detail = firstLine(run.detail);
  const outcome = run.endReason === null ? t('run-live') : t(`outcome.${run.endReason}`);

  return (
    <li>
      <button
        type="button"
        aria-pressed={selected}
        aria-label={t('run-label', { worker: run.worker, time: moment(run.claimedAt) })}
        onClick={onSelect}
        className={cn(
          'flex w-full min-w-0 flex-col gap-1 rounded-md border border-solid px-3 py-2 text-left',
          selected ? 'border-interactive bg-canvas' : 'border-border bg-surface hover:bg-hover-bg',
        )}
      >
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm text-text-primary">
          <StatusBadge
            label={outcome}
            tone={run.endReason === null ? 'info' : run.endReason === 'finished' ? 'success' : 'neutral'}
            size="sm"
          />
          <span className="font-mono text-xs">{run.lane ?? '—'}</span>
          <span className="min-w-0 truncate text-xs text-text-secondary">
            {run.worker} · {run.machineName}
          </span>
        </span>
        <span className="flex flex-wrap gap-x-3 text-xs text-text-tertiary">
          <span>{moment(run.claimedAt)}</span>
          <span>{t('duration', { duration: formatDuration(runDurationMs(run, now)) })}</span>
          {usage.unknown ? (
            <span>{t('usage-unknown')}</span>
          ) : (
            <span>
              {t('tokens', { count: number(usage.tokens) })} · {euro(usage.cost)}
            </span>
          )}
        </span>
        {detail !== null && <span className="min-w-0 truncate text-xs text-text-secondary">{detail}</span>}
      </button>
    </li>
  );
}

/**
 * A ticket's details, laid over the right of the board (the machine panel's pattern): what the board knows of the card
 * now, and what the fleet knows of its runs, with the selected run's messages, timeline and log. A labelled `aside`,
 * not a dialog: the board stays usable and polling behind it.
 */
export function TicketPanel({ ticket, found, refreshKey, canCommand, onClose, onOpenTicket }: Props) {
  const t = useTranslations('pages.admin.fleet-board');
  const tp = useTranslations('pages.admin.fleet-board.panel');
  const { moment } = useLocaleFormat();
  const [read, setRead] = useState<TicketRead>({ ticket, data: null, failed: false });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Bumped by an answer or a release here: the ticket is re-read at once rather than at the next board poll.
  const [reloads, setReloads] = useState(0);
  const reload = () => setReloads((count) => count + 1);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) onClose();
    };

    document.addEventListener('keydown', onKey);

    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Previous data stays while a re-read is in flight; another ticket's data never shows for this one.
  useEffect(() => {
    let cancelled = false;

    void apiClient.adminFleetBoard.ticket({ pathParams: { ticket } }).then(
      (result) => {
        if (cancelled) return;

        setRead((previous) => ({
          ticket,
          data: result.ok ? result.body : previous.ticket === ticket ? previous.data : null,
          failed: !result.ok,
        }));
      },
      () => {
        if (!cancelled)
          setRead((previous) => ({ ticket, data: previous.ticket === ticket ? previous.data : null, failed: true }));
      },
    );

    return () => {
      cancelled = true;
    };
  }, [ticket, refreshKey, reloads]);

  useEffect(() => setSelectedId(null), [ticket]);

  const data = read.ticket === ticket ? read.data : null;
  const failed = read.ticket === ticket && read.failed;
  const runs = data?.runs ?? [];
  const live = runs.find((run) => run.endedAt === null) ?? null;
  const selected = runs.find((run) => run.jobId === selectedId) ?? runs[0] ?? null;
  const card = found?.card ?? null;
  const now = Date.now();
  const job = card?.job ?? null;
  const limit = card?.history.limit ?? FLEET_JOB_MAX_RUNS;
  const used = card?.history.runs ?? runs.filter((run) => run.endReason !== 'no-run').length;
  const url = card?.url;
  // A question is moot once a later run of its key was claimed (newest first): the backend refuses its answer.
  const selectedOpen =
    selected !== null && !runs.slice(0, runs.indexOf(selected)).some((run) => run.key === selected.key);

  return (
    <aside
      aria-label={ticket}
      className="fixed inset-y-0 right-0 z-30 flex w-full max-w-[640px] flex-col border-l border-border bg-surface shadow-xl"
    >
      <div className="flex shrink-0 items-start gap-2 border-b border-neutral-100 px-6 py-4">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h2 className="flex min-w-0 items-center gap-2 text-lg font-semibold text-text-primary">
            {url === undefined ? (
              <span className="font-mono">{ticket}</span>
            ) : (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('linear-label', { ticket })}
                title={t('linear')}
                className="inline-flex items-center gap-1 font-mono hover:underline"
              >
                {ticket}
                <ExternalLinkIcon aria-hidden className="h-3.5 w-3.5" />
              </a>
            )}
          </h2>
          {card !== null && <p className="break-words text-sm text-text-secondary">{card.title}</p>}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={tp('close')}
          title={tp('close')}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-text-tertiary hover:bg-hover-bg"
        >
          <XIcon className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex flex-col gap-6 px-6 py-5">
          {card === null ? (
            <p className="text-sm text-text-secondary">{tp('off-board')}</p>
          ) : (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <Field label={tp('lane')}>
                <span className="font-mono">{found?.lane}</span>
              </Field>
              <Field label={tp('state')}>{card.state}</Field>
              <Field label={tp('tag')}>
                <CardTag tag={card.tag} />
              </Field>
              <Field label={tp('priority')}>{t(`priority.${String(card.priority)}`)}</Field>
              <Field label={tp('order')}>
                {card.rank === null
                  ? tp('order-linear')
                  : found?.position == null
                    ? tp('order-manual-unqueued')
                    : tp('order-manual', { position: found.position })}
              </Field>
              <Field label={tp('runs')}>{tp('runs-value', { runs: used, limit })}</Field>
            </dl>
          )}

          <section className="flex flex-col gap-2" aria-label={tp('current-heading')}>
            <h3 className="text-sm font-semibold text-text-primary">{tp('current-heading')}</h3>
            {live === null && job === null ? (
              <p className="text-sm text-text-secondary">{tp('no-current')}</p>
            ) : (
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Field label={tp('worker')}>{live?.worker ?? job?.worker}</Field>
                <Field label={tp('machine')}>{live?.machineName ?? job?.machine}</Field>
                <Field label={tp('claimed')}>{moment(live?.claimedAt ?? job?.claimedAt ?? '')}</Field>
                <Field label={tp('last-beat')}>
                  {(() => {
                    const beat = live?.lastBeatAt ?? job?.lastBeatAt ?? null;

                    return beat === null ? '—' : moment(beat);
                  })()}
                </Field>
                {(live?.silent === true || card?.tag === 'silent') && (
                  <Field label={tp('silent')}>
                    <StatusBadge label={tp('silent')} tone="danger" size="sm" />
                  </Field>
                )}
              </dl>
            )}
            {canCommand && live !== null && <ReleaseJob key={live.jobId} jobId={live.jobId} onReleased={reload} />}
          </section>

          {card !== null && <Blockers blockers={card.blockers} onOpen={onOpenTicket} />}

          {/* Questions lead the run log: an open one waits on an answer, the log is only history. */}
          {selected !== null && (
            <Questions
              key={`questions-${selected.jobId}`}
              questions={selected.questions}
              ticket={ticket}
              open={selectedOpen}
              canCommand={canCommand}
              onResolved={reload}
            />
          )}

          <section className="flex flex-col gap-2" aria-label={tp('history-heading')}>
            <h3 className="text-sm font-semibold text-text-primary">{tp('history-heading')}</h3>
            {data === null && !failed && (
              <div role="status" aria-label={tp('loading')}>
                <Spinner size="sm" />
              </div>
            )}
            {failed && <p className="text-sm text-danger-700">{tp('load-failed')}</p>}
            {data !== null && runs.length === 0 && <p className="text-sm text-text-secondary">{tp('history-empty')}</p>}
            {runs.length > 0 && (
              <ul className="flex flex-col gap-2">
                {runs.map((run) => (
                  <RunRow
                    key={run.jobId}
                    run={run}
                    now={now}
                    selected={selected?.jobId === run.jobId}
                    onSelect={() => setSelectedId(run.jobId)}
                  />
                ))}
              </ul>
            )}
          </section>

          {selected !== null && (
            <RunDetail key={`run-${selected.jobId}`} run={selected} canCommand={canCommand} refreshKey={refreshKey} />
          )}
        </div>
      </div>
    </aside>
  );
}
